<template>
  <div>
    <div class="clearfix mb-4">
      <h4 class="card-title float-start">{{ $t('maintenance.frontPage.title.infoServiceInt') }}</h4>
      <div class="float-end d-flex flex-row">
        <McButton
          v-if="canShowActionButtons"
          variant="secondary"
          class="me-2"
          :text="$t('global.cancel')"
          :icon="'icon-outline_close'"
          @click="handleCancel"
        />
        <McButton
          v-if="canShowActionButtons"
          variant="primary"
          :text="$t('global.save')"
          :icon="'icon-outline_save'"
          :disabled="disableButton"
          @click="handleSave"
        />
      </div>
    </div>
    <b-card>
      <FormulateForm v-model="formInfo" ref="formRef">
        <b-row>
          <b-col md="3" class="mb-3">
            <FormulateInput
              type="value-help"
              name="licensePlateSelect"
              :placeholder="`${$t('global.form.select')} ${$t('global.object.licensePlate')}`"
              :listGetter="resourcefulQuery.licensePlate"
              :tableFields="tableFields.licensePlate"
              :trackBy="'id'"
              :displayedBy="'licensePlate'"
              :initialObject="initialObject.vehicle"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              :modalTitle="$t('global.object.licensePlate')"
              :disabled="initialObject.vehicle !== null"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('global.object.licensePlate') }} <i class="text-danger">*</i></label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col md="3" class="mb-3">
            <FormulateInput type="group" :label="$t('maintenance.order.serviceLocationType')">
              <div class="mt-2">
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    id="internal"
                    type="radio"
                    name="locationTypeOptions"
                    v-model="formInfo.locationType"
                    :value="'Internal'"
                    @click="positionName = ['Workshop']"
                    :disabled="isMGMAndMO ? MGMDisabled('locationTypeOptions') : isAllDisabled"
                  />
                  <label class="form-check-label" for="internal">{{
                    $t('maintenance.order.form.internal')
                  }}</label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    id="eksternal"
                    type="radio"
                    v-model="formInfo.locationType"
                    :value="'Eksternal'"
                    name="locationTypeOptions"
                    @click="positionName = ['Workshop', 'Driver']"
                    :disabled="isMGMAndMO ? MGMDisabled('locationTypeOptions') : isAllDisabled"
                  />
                  <label class="form-check-label" for="eksternal">{{
                    $t('maintenance.order.form.external')
                  }}</label>
                </div>
              </div>
            </FormulateInput>
          </b-col>
          <b-col md="3" class="mb-3">
            <FormulateInput
              type="select-resourceful-v2"
              name="serviceLocationSelect"
              :placeholder="`${$t('global.form.select')} ${$t(
                'maintenance.order.serviceLocation'
              )}`"
              :trackBy="'geofenceId'"
              :optionsGetter="resourcefulQuery.serviceLocation"
              :optionLabelFn="({ item }) => item.fenceName"
              :fullObject="fullObject.serviceLocation"
              validation="required:trim"
              error-behavior="blur"
              validation-behavior="blur"
              v-model="formInfo.serviceLocationSelect"
              @addTag="tagCustom"
              :multiple="false"
              :taggable="formInfo.locationType == 'Eksternal' ? true : false"
              :disabled="isMGMAndMO ? MGMDisabled('serviceLocationSelect') : isAllDisabled"
              tabIndex="0"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label
                    >{{ $t('maintenance.order.serviceLocation') }}
                    <i class="text-danger">*</i></label
                  >
                </div>
              </template>
            </FormulateInput>
          </b-col>

          <b-col md="3">
            <FormulateInput
              type="select-resourceful"
              name="pic"
              :placeholder="`${$t('global.form.select')} ${$t('maintenance.order.form.pic')}`"
              :optionsGetter="resourcefulQuery.employees"
              :trackBy="'userId'"
              :optionLabelFn="({ item }) => item.fullname"
              :initialObject="initialObject.pic"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              v-model="formInfo.pic"
              :visibleValidationErrors="true"
              :disabled="isMGMAndMO ? MGMDisabled('pic') : isAllDisabled"
              tabIndex="0"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('maintenance.order.form.pic') }} <i class="text-danger">*</i></label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col v-if="isMGMAndMO" md="3">
            <FormulateInput
              v-model="formInfo.mechanicName"
              type="text"
              name="mechanicName"
              :validation-name="$t('maintenance.order.form.mechanicName')"
              :placeholder="$t('maintenance.order.form.mechanicNamePlaceholder')"
              validation="required|max:128,length"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              :disabled="MGMDisabled('mechanicName')"
              tabIndex="0"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>
                    {{ $t('maintenance.order.form.mechanicName') }}
                    <i class="text-danger">*</i>
                  </label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col md="3">
            <FormulateInput
              name="startServicePeriode"
              :label="$t('maintenance.order.form.startDate')"
              :placeholder="$t('maintenance.order.form.startDate')"
              type="date-picker"
              datepickerType="datetime"
              format="DD MMM YYYY - HH:mm"
              value-type="YYYY-MM-DD HH:mm"
              :disabled="isMGMAndMO ? MGMDisabled('startServicePeriode') : true"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              name="etaServicePeriode"
              :label="$t('maintenance.order.form.etaTime')"
              :placeholder="
                isMGMAndMO
                  ? $t('maintenance.order.form.etaTime')
                  : $t('maintenance.order.form.enableInprogressPlaceholder')
              "
              type="date-picker"
              datepickerType="datetime"
              format="DD MMM YYYY - HH:mm"
              value-type="YYYY-MM-DD HH:mm"
              :validation="etaServicePeriodeValidation"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              :disabled="
                isMGMAndMO
                  ? MGMDisabled('etaServicePeriode')
                  : (isAllDisabled && isEtaDisabled) || status == 'Draft'
              "
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>
                    {{ $t('maintenance.order.form.etaTime') }}
                    <i v-if="isEtaServicePeriodeRequired" class="text-danger">*</i>
                  </label>
                </div>
              </template>
            </FormulateInput>
          </b-col>
          <b-col md="3" v-if="status === 'Finished'">
            <FormulateInput
              name="actualEndTime"
              :label="$t('maintenance.order.actualEndTime')"
              :placeholder="$t('maintenance.order.actualEndTime')"
              type="date-picker"
              datepickerType="datetime"
              format="DD MMM YYYY - HH:mm"
              value-type="YYYY-MM-DD HH:mm"
              :showSecond="false"
              :disabled="isMGMAndMO ? MGMDisabled('actualEndTime') : true"
            />
          </b-col>
          <b-col v-if="isMGMAndMO" md="3">
            <FormulateInput
              v-model="formInfo.maintenanceType"
              class="maintenance-type-input"
              type="select-resourceful"
              name="maintenanceType"
              trackBy="value"
              :initialObject="maintenanceTypeInitialObject"
              validation="required"
              error-behavior="blur"
              validation-behavior="blur"
              :visibleValidationErrors="true"
              :multiple="true"
              :label="$t('maintenance.order.form.maintenanceType')"
              :placeholder="$t('maintenance.order.form.maintenanceTypePlaceholder')"
              :optionsGetter="resourcefulQuery.maintenanceTypes"
              :optionLabelFn="({ item }) => item.label"
              :searchable="false"
              :disabled="MGMDisabled('maintenanceType')"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label
                    >{{ $t('maintenance.order.form.maintenanceType') }}
                    <i class="text-danger">*</i></label
                  >
                </div>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
      </FormulateForm>

      <h4 class="card-title my-3">{{ $t('master.form.fleet.fleetInformation') }}</h4>
      <b-row class="mc-list-info__top border py-3 px-2 mx-1 rounded">
        <b-col class="border-end">
          <span>{{ $t('global.field.vehicleType') }}</span>
          <strong>{{ serviceOrder?.vehicle?.vehicleType?.type ?? '-' }}</strong>
        </b-col>

        <b-col class="border-end">
          <span>{{ $t('global.object.fleetCategory') }}</span>
          <strong>{{ serviceOrder?.vehicle?.vehicleType?.category ?? '-' }}</strong>
        </b-col>

        <b-col class="border-end">
          <span>{{ $t('setting.list.fleetSetting.fleetModel') }}</span>
          <strong>{{ serviceOrder?.vehicle?.vehicleType?.model ?? '-' }}</strong>
        </b-col>

        <b-col :class="totalHourmeter > 0 ? 'border-end' : ''">
          <span v-if="serviceOrder?.status !== 'Finished'">{{
            $t('maintenance.field.odoFleetNow')
          }}</span>
          <span v-else>{{ $t('maintenance.order.odoFleetService') }}</span>
          <strong v-if="render">
            {{
              status == 'Draft' || status == 'Confirmed'
                ? thousandFormat(serviceOrder?.vehicle?.odometer)
                : thousandFormat(parseFloat(serviceOrder?.odometer/1000))
            }}
            Km</strong
          >
          <i
            v-if="status == 'Draft' || status == 'Confirmed'"
            id="odoNow"
            v-b-tooltip.hover.bottom="'Refresh'"
            class="mc-fa ms-2 icon-fill_sync"
            @click="handleRefresh"
          ></i>
        </b-col>
        <b-col v-if="totalHourmeter > 0">
          <span v-if="serviceOrder?.status !== 'Finished'">{{
            $t('maintenance.field.hourmeterFleetNow')
          }}</span>
          <span v-else>
            {{ $t('maintenance.order.hourmeterFleetService') }}
          </span>
          <strong v-if="render">
            {{
              status == 'Draft' || status == 'Confirmed'
                ? minuteToHour(serviceOrder?.vehicle?.hourmeter)
                : minuteToHour(serviceOrder?.hourmeter)
            }}
          </strong>
          <i
            id="hourmeterNow"
            v-b-tooltip.hover.bottom="'Refresh'"
            class="mc-fa ms-2 icon-fill_sync"
            @click="handleRefresh"
          ></i>
        </b-col>
        <b-col
          v-if="isMGMAndMO && totalRefrigerator > 0 && 
          (serviceOrder?.refrigerator !== null || serviceOrder?.vehicle?.refrigerator !== null)"
        >
          <span v-if="serviceOrder?.status !== 'Finished'">{{
            $t('maintenance.field.refrigeratorFleetNow')
          }}</span>
          <span v-else>
            {{ $t('maintenance.order.refrigeratorFleetService') }}
          </span>
          <strong v-if="render">
            {{
              status == 'Draft' || status == 'Confirmed'
                ? minuteToHour(serviceOrder?.vehicle?.refrigerator)
                : minuteToHour(serviceOrder?.refrigerator)
            }}
          </strong>
          <i
            id="refrigeratorNow"
            v-b-tooltip.hover.bottom="'Refresh'"
            class="mc-fa ms-2 icon-fill_sync"
            @click="handleRefresh"
          ></i>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import appConfig from '@/app.config'
import { GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import { GET_EMPLOYEES_PAGINATED } from '@/services/api/master/employees'
import { GET_GEOFENCES } from '@/services/api/master/geofence'
import McButton from '@/components/McButton'
import { GET_SERVICE_ORDER, UPDATE_SERVICE_ORDER } from '@/services/api/maintenance/serviceorder'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    McButton,
  },

  mixins: [Helper],

  apollo: {
    serviceOrder: {
      query: GET_SERVICE_ORDER,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            serviceOrderId: +this.$route.params.id,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        if (data) {
          this.syncFormWithServiceOrder(data.serviceOrder.serviceOrder)
        }
      },
    },
  },
  props: ['status', 'totalKilometer', 'totalHourmeter', 'totalRefrigerator'],

  data() {
    return {
      formInfo: {
        licensePlateSelect: null,
        serviceLocationSelect: null,
        locationType: null,
        pic: null,
        mechanicName: '',
        maintenanceType: null,
        startServicePeriode: '',
        etaServicePeriode: '',
        actualEndTime: '',
        selectedServiceLocation: null,
        serviceLocationKey: 'geofenceId',
        oldObject: null,
      },
      selectedServiceLocation: null,
      serviceLocationFromBE: null,
      serviceLocationKey: 'geofenceId',
      rendered: true,
      isUpdate: true,
      initialObject: {
        pic: null,
        serviceLocation: null,
        vehicle: null,
      },
      serviceLocation: [],
      positionName: [],
      oldValues: {},
      newValues: {},
      isSaveDisabled: false,
      isAllDisabled: false,
      isEtaDisabled: true,
      serviceOrder: {},
      render: true,
      disableButton: true,
      isSyncingServiceOrder: false,
      fullObject: {
        serviceLocation: {},
      },
      maintenanceTypeOptions: [
        { value: 'Breakdown', label: 'Breakdown' },
        { value: 'General Issue / Routine', label: 'General Issue / Routine' },
        { value: 'Preventive', label: 'Preventive' },
      ],
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    maintenanceTypeInitialObject() {
      if (!Array.isArray(this.formInfo.maintenanceType)) return []

      return this.formInfo.maintenanceType
        .map((value) => {
          if (value && typeof value === 'object' && value.value !== undefined) {
            return value
          }

          const option = this.maintenanceTypeOptions.find(
            (item) => String(item.value) === String(value)
          )

          if (option) return option

          return value
            ? {
                value,
                label: value,
              }
            : null
        })
        .filter(Boolean)
    },
    resourcefulQuery() {
      return {
        maintenanceTypes: () => ({
          metadata: {
            total_count: 3,
            page: 1,
          },
          data: {
            paginated_result: this.maintenanceTypeOptions,
          },
          isPagination: false,
        }),
        licensePlate: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            parentOnly: true,
            orderBy: opts?.sorts,
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_VEHICLES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehiclesPaginated.vehiclesPaginated

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },

        serviceLocation: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_GEOFENCES,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                isServiceLoc: true,
                serviceLocType: this.formInfo.locationType ?? ['Internal', 'Eksternal'],
              },
            },
          })

          const locationOptions = res.data.geofences.geofences
          const self = this
          self.serviceLocation = locationOptions.data
          self.initialObject.serviceLocation = self.serviceLocation

          result = {
            metadata: {
              total_count: locationOptions.pagination.total,
              page: locationOptions.pagination.page,
            },
            data: {
              paginated_result: locationOptions.data,
            },
          }

          return result
        },

        employees: async (opts) => {
          let result = {}
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_EMPLOYEES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                positionName: this.positionName,
              },
            },
          })

          const employees = res.data.employeesPaginated.employeesPaginated

          result = {
            metadata: {
              total_count: employees.pagination.total,
              page: employees.pagination.page,
            },
            data: {
              paginated_result: employees.data,
            },
          }

          return result
        },
      }
    },
    isEtaServicePeriodeRequired() {
      return this.isMGMAndMO && ['Draft', 'Confirmed'].includes(this.status)
    },
    etaServicePeriodeValidation() {
      return this.isEtaServicePeriodeRequired ? 'required' : ''
    },
    canShowActionButtons() {
      if (this.isMGMAndMO) return ['Draft', 'Confirmed', 'InProgress'].includes(this.status)
      return !(this.isEtaDisabled && this.isAllDisabled)
    },
    tableFields() {
      return {
        licensePlate: [
          {
            key: 'licensePlate',
            label: this.$t('global.object.licensePlate'),
          },
          {
            key: 'vehicleType.type',
            label: this.$t('global.object.fleetType'),
          },
          {
            key: 'vehicleType.category',
            label: this.$t('global.object.fleetCategory'),
          },
          {
            key: 'vehicleType.model',
            label: this.$t('global.object.fleetModel'),
          },
        ],
      }
    },
  },

  watch: {
    'formInfo.locationType': {
      handler(newValue, oldValue) {
        this.positionName = newValue === 'Internal' ? ['Workshop'] : ['Workshop', 'Driver']

        if (this.isSyncingServiceOrder) return
        if (oldValue === null || oldValue === undefined || newValue === oldValue) return

        if (newValue === this.serviceOrder.locationType && this.serviceLocationFromBE) {
          if (
            this.serviceLocationFromBE.geofenceId !== 0 &&
            !this.serviceLocation.some(
              (item) => item.geofenceId === this.serviceLocationFromBE.geofenceId
            )
          ) {
            this.serviceLocation = [this.serviceLocationFromBE, ...this.serviceLocation]
          }

          this.formInfo.serviceLocationSelect = this.serviceOrder.geofenceId ?? 0
          this.selectedServiceLocation = this.serviceLocationFromBE
          this.fullObject.serviceLocation = this.serviceLocationFromBE
          this.serviceLocationKey = this.serviceOrder.geofenceId !== null ? 'geofenceId' : 'address'
          this.formInfo.serviceLocationKey = this.serviceLocationKey
          return
        }

        this.formInfo.serviceLocationSelect = null
        this.selectedServiceLocation = null
        this.fullObject.serviceLocation = null
        this.serviceLocationKey = 'geofenceId'
        this.formInfo.serviceLocationKey = 'geofenceId'
      },
    },

    'formInfo.serviceLocationSelect': {
      handler(e) {
        if (this.isSyncingServiceOrder) return

        const selectedNewValues = this.serviceLocation.filter((item) => item.geofenceId === e)
        if (selectedNewValues.length !== 0) {
          this.fullObject.serviceLocation = selectedNewValues[0]
          this.selectedServiceLocation = selectedNewValues[0]
        }

        if (e !== 0) this.serviceLocationKey = 'geofenceId'
        else this.serviceLocationKey = 'address'
      },
    },

    formInfo: {
      deep: true,
      handler(value) {
        const requiredEmpty =
          !value.licensePlateSelect ||
          !value.locationType ||
          value.serviceLocationSelect === null ||
          value.serviceLocationSelect === undefined ||
          !value.pic ||
          (this.isMGMAndMO && (!value.mechanicName || !value.maintenanceType?.length))

        const exceedMaxMechanicName = this.isMGMAndMO && value.mechanicName && value.mechanicName.length > 128
        if (requiredEmpty || exceedMaxMechanicName) {
          this.disableButton = true
          return
        }

        if (this.isUpdate) {
          if (Object.entries(this.oldValues).toString() !== Object.entries(value).toString()) {
            this.disableButton = false
          } else this.disableButton = true
        } else this.disableButton = false
      },
    },

    serviceOrder: {
      handler(e) {
        this.isAllDisabled = e.status != 'Draft'
        this.isEtaDisabled = e.status != 'InProgress'
      },
    },
    status: {
      handler(e) {
        this.isAllDisabled = e != 'Draft'
        this.isEtaDisabled = e != 'InProgress'
      },
      immediate: true,
    },
  },

  mounted() {
    if (this.serviceOrder.geofenceId !== null) this.formInfo.serviceLocationKey = 'geofenceId'
    else this.formInfo.serviceLocationKey = 'address'

    this.isAllDisabled = this.status != 'Draft'
    this.isEtaDisabled = this.status != 'InProgress'
  },

  methods: {
    validateEtaServicePeriodeForInProgress() {
      if (!this.isEtaServicePeriodeRequired) {
        return { valid: true }
      }

      this.$refs.formRef.showErrors()

      if (!this.formInfo.etaServicePeriode || this.$refs.formRef.hasErrors) {
        return { valid: false, reason: 'required' }
      }

      const savedEtaServicePeriode =
        this.oldValues?.etaServicePeriode ||
        (this.serviceOrder.estimatedEndTime
          ? this.momentYmDHm(this.serviceOrder.estimatedEndTime)
          : '')

      if (savedEtaServicePeriode !== this.formInfo.etaServicePeriode) {
        return { valid: false, reason: 'unsaved' }
      }

      return { valid: true }
    },

    buildServiceLocationFromBE(serviceOrder = this.serviceOrder) {
      if (!serviceOrder) return null

      return serviceOrder.geofenceId !== null
        ? serviceOrder.geofence
        : {
            address: serviceOrder.address,
            fenceName: serviceOrder.address,
            geofenceId: 0,
          }
    },

    createOldValuesSnapshot() {
      return {
        licensePlateSelect: this.formInfo.licensePlateSelect,
        serviceLocationSelect: this.formInfo.serviceLocationSelect,
        locationType: this.formInfo.locationType,
        pic: this.formInfo.pic,
        mechanicName: this.formInfo.mechanicName,
        maintenanceType: Array.isArray(this.formInfo.maintenanceType)
          ? this.formInfo.maintenanceType.map((item) =>
              item && typeof item === 'object' ? { ...item } : item
            )
          : this.formInfo.maintenanceType,
        startServicePeriode: this.formInfo.startServicePeriode,
        etaServicePeriode: this.formInfo.etaServicePeriode,
        actualEndTime: this.formInfo.actualEndTime,
        selectedServiceLocation: this.selectedServiceLocation
          ? { ...this.selectedServiceLocation }
          : null,
        serviceLocationKey: this.formInfo.serviceLocationKey,
        oldObject: this.fullObject.serviceLocation ? { ...this.fullObject.serviceLocation } : null,
      }
    },

    syncFormWithServiceOrder(serviceOrder) {
      if (!serviceOrder?.vehicle) return

      this.isSyncingServiceOrder = true
      this.serviceOrder = serviceOrder

      const serviceLocationFromBE = this.buildServiceLocationFromBE(serviceOrder)
      const serviceLocationKey = serviceOrder.geofenceId !== null ? 'geofenceId' : 'address'

      this.serviceLocationFromBE = serviceLocationFromBE
      this.selectedServiceLocation = serviceLocationFromBE
      this.fullObject.serviceLocation = serviceLocationFromBE
      this.initialObject.vehicle = serviceOrder.vehicle
      this.initialObject.pic = serviceOrder.pic
      this.serviceLocationKey = serviceLocationKey
      this.positionName =
        serviceOrder.locationType === 'Internal' ? ['Workshop'] : ['Workshop', 'Driver']

      if (
        serviceLocationFromBE?.geofenceId !== 0 &&
        serviceLocationFromBE?.geofenceId !== undefined &&
        !this.serviceLocation.some((item) => item.geofenceId === serviceLocationFromBE.geofenceId)
      ) {
        this.serviceLocation = [serviceLocationFromBE, ...this.serviceLocation]
      }

      this.formInfo = {
        ...this.formInfo,
        mechanicName: serviceOrder.mechanicName,
        maintenanceType: serviceOrder.maintenanceType,
        licensePlateSelect: serviceOrder.vehicle.id,
        locationType: serviceOrder.locationType,
        serviceLocationSelect: serviceOrder.geofenceId ?? 0,
        pic: serviceOrder.picId,
        startServicePeriode: serviceOrder.startTime
          ? this.momentYmDHm(serviceOrder.startTime)
          : serviceOrder.startTime,
        etaServicePeriode: serviceOrder.estimatedEndTime
          ? this.momentYmDHm(serviceOrder.estimatedEndTime)
          : serviceOrder.estimatedEndTime,
        actualEndTime: serviceOrder.actualEndTime
          ? this.momentYmDHm(serviceOrder.actualEndTime)
          : serviceOrder.actualEndTime,
        selectedServiceLocation: serviceLocationFromBE,
        serviceLocationKey: serviceLocationKey,
        oldObject: serviceLocationFromBE,
      }

      this.oldValues = this.createOldValuesSnapshot()
      this.disableButton = true
      this.isAllDisabled = serviceOrder.status != 'Draft'
      this.isEtaDisabled = serviceOrder.status != 'InProgress'

      this.$nextTick(() => {
        this.isSyncingServiceOrder = false
      })
    },

    MGMDisabled(field_name) {
      if (['Draft', 'Confirmed', 'InProgress'].includes(this.status)) return false
      return true
    },
    tagCustom(newTag) {
      this.serviceLocation = this.serviceLocation.filter((item) => item.geofenceId !== 0)

      const tag = {
        address: newTag,
        fenceName: newTag,
        geofenceId: 0,
      }

      this.serviceLocation.push(tag)
      this.selectedServiceLocation = tag
      this.fullObject.serviceLocation = tag

      this.formInfo.serviceLocationSelect = tag.geofenceId
    },

    async handleSave() {
      this.newValues = this.createOldValuesSnapshot()

      const oldLocationObject =
        this.oldValues.oldObject ||
        this.fullObject.serviceLocation ||
        (this.serviceOrder.geofenceId !== null
          ? this.serviceOrder.geofence
          : {
              address: this.serviceOrder.address,
              fenceName: this.serviceOrder.address,
              geofenceId: 0,
            })

      const oldData = {
        [this.oldValues.serviceLocationKey]:
          this.oldValues.oldObject[this.oldValues.serviceLocationKey],
        locationType: this.oldValues.locationType,
        picId: this.oldValues.pic,
        mechanicName: this.oldValues.mechanicName,
        maintenanceType: this.oldValues.maintenanceType,
        vehicleId: this.oldValues.licensePlateSelect,
        estimatedEndTime: this.oldValues.etaServicePeriode
          ? new Date(this.oldValues.etaServicePeriode).toISOString()
          : null,
      }

      const deleteGeofence = this.serviceLocationKey === 'address' ? { geofenceId: null } : {}

      const newData = {
        [this.serviceLocationKey]: this.selectedServiceLocation
          ? this.selectedServiceLocation[this.serviceLocationKey]
          : oldLocationObject[this.serviceLocationKey],
        ...deleteGeofence,
        locationType: this.newValues.locationType,
        picId: this.newValues.pic,
        mechanicName: this.newValues.mechanicName,
        maintenanceType: this.newValues.maintenanceType,
        vehicleId: this.newValues.licensePlateSelect,
        estimatedEndTime: this.newValues.etaServicePeriode
          ? new Date(this.newValues.etaServicePeriode).toISOString()
          : null,
      }

      const input = {
        serviceOrderId: this.serviceOrder.serviceOrderId,
        oldData: oldData,
        newData: newData,
      }

      const res = await this.$apollo.mutate({
        mutation: UPDATE_SERVICE_ORDER,
        variables: {
          input: input,
        },
      })

      if (res.data.updateServiceOrder.isSuccessful) {
        const currentLocationObject =
          this.selectedServiceLocation || this.fullObject.serviceLocation || oldLocationObject

        this.fullObject.serviceLocation = currentLocationObject
        this.oldValues = JSON.parse(JSON.stringify(this.formInfo))
        this.oldValues.oldObject = JSON.parse(JSON.stringify(currentLocationObject))
        this.serviceOrder.estimatedEndTime = newData.estimatedEndTime
        this.disableButton = true
        await this.$apollo.queries.serviceOrder.refetch()
        this.$mcToast.success(this.$t('global.alert.text.changed'))
        this.$emit('save-success')
      } else {
        this.$mcToast.error(res.data.updateServiceOrder.message)
      }
    },

    handleCancel() {
      this.formInfo = {
        licensePlateSelect: null,
        serviceLocationSelect: null,
        locationType: null,
        pic: null,
        mechanicName: '',
        maintenanceType: [],
        startServicePeriode: '',
        etaServicePeriode: '',
        selectedServiceLocation: null,
        serviceLocationKey: 'geofenceId',
        oldObject: null,
      }
      this.serviceLocationFromBE = null
      this.$router.push({
        name: 'Service Order List',
      })
    },

    handleRefresh() {
      this.render = false

      this.$apollo.queries.serviceOrder.refetch()
      setTimeout(() => {
        this.render = true
      }, 500)
    },
  },
}
</script>

<style lang="scss">
.maintenance-type-input {
  .formulate-input-element--select-resourceful,
  .multiselect__tags {
    height: auto;
    min-height: 37px !important;
  }

  .multiselect__tags {
    max-height: none;
  }
}
</style>
